"use client";

import { Hand, Users } from "lucide-react";
import { useState } from "react";
import { TextLinkCTA } from "~/components/siteglue/siteglue-cta";
import { HOME_SIGNATURE_CHANNELS } from "~/components/siteglue/home/home-agent-signature";
import {
  FadeIn,
  HomeEyebrow,
  HomeHeading,
  HomeLead,
  HomeSection,
} from "~/components/siteglue/home/section-shell";
import { SITEGLUE_CHANNEL_IDENTITY_NOTE } from "~/lib/siteglue/communication-channels";
import { SITEGLUE_HITL_LINE } from "~/lib/siteglue/marketing-chrome";
import { MARKETING_PRODUCT_RESPONSIBILITIES } from "~/lib/inbound-gpt/paths";

export function SiteGlueHomeHitl({
  layout = "default",
}: {
  layout?: "default" | "compact";
} = {}) {
  const channels = HOME_SIGNATURE_CHANNELS.filter(
    (row) => row.id !== "whatsapp" && row.id !== "messenger",
  );
  const [open, setOpen] = useState<string | null>(channels[0]?.label ?? null);

  if (layout === "compact") {
    return (
      <HomeSection id="human-in-the-loop" tone="slate" className="py-14 sm:py-16">
        <FadeIn className="mx-auto max-w-2xl text-center">
          <HomeEyebrow>Human-in-the-Loop™</HomeEyebrow>
          <HomeHeading className="mx-auto">Team Takeover™ when it matters.</HomeHeading>
          <HomeLead className="mx-auto">
            {SITEGLUE_HITL_LINE} Lead Team routing keeps the conversation with the right person.
          </HomeLead>
        </FadeIn>
        <div className="mx-auto mt-8 max-w-2xl divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
          {channels.map((row) => {
            const isOpen = open === row.label;
            return (
              <div key={row.label}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-slate-800"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : row.label)}
                >
                  <span className="inline-flex items-center gap-2">
                    <row.Icon className="h-4 w-4 text-[#4f81bd]" aria-hidden />
                    {row.label}
                  </span>
                  <span className="text-xs text-slate-400">{isOpen ? "Hide" : "Team Takeover™"}</span>
                </button>
                {isOpen ? <p className="px-4 pb-3 text-sm text-slate-600">{row.takeover}</p> : null}
              </div>
            );
          })}
        </div>
      </HomeSection>
    );
  }

  return (
    <HomeSection id="human-in-the-loop" tone="slate">
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <FadeIn>
          <HomeEyebrow>Human-in-the-Loop™</HomeEyebrow>
          <HomeHeading>Autonomous when it should be. Human when it matters.</HomeHeading>
          <HomeLead>
            {SITEGLUE_HITL_LINE} Lead Team routing keeps the conversation with the right person.
          </HomeLead>
          <div className="mt-8 flex flex-wrap gap-2">
            {channels.map(({ label, Icon }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
              >
                <Icon className="h-3.5 w-3.5 text-[#4f81bd]" aria-hidden />
                {label}
              </span>
            ))}
          </div>
          <div className="mt-8">
            <TextLinkCTA
              action="discoverPlatform"
              href={MARKETING_PRODUCT_RESPONSIBILITIES}
              location="feature_section"
              label="See how handoff works"
            />
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-8 shadow-sm">
            <div className="flex items-center justify-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-[#4f81bd]/25 bg-[#4f81bd]/5 text-lg font-semibold text-[#4f81bd]">
                AI
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#9cbb5a]/15 text-[#9cbb5a]">
                <Hand className="h-4 w-4" aria-hidden />
              </div>
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
                <Users className="h-7 w-7 text-slate-700" aria-hidden />
              </div>
            </div>
            <p className="mt-6 text-center text-xs font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              Team Takeover™ by channel
            </p>
            <div className="mt-4 space-y-3">
              {channels.map((row) => (
                <div
                  key={row.label}
                  className="rounded-xl bg-slate-50 px-4 py-3 text-sm"
                >
                  <p className="inline-flex items-center gap-2 font-medium text-slate-800">
                    <row.Icon className="h-4 w-4 text-[#4f81bd]" aria-hidden />
                    {row.label}
                  </p>
                  <p className="mt-1 text-slate-600">{row.takeover}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              {SITEGLUE_CHANNEL_IDENTITY_NOTE}
            </p>
          </div>
        </FadeIn>
      </div>
    </HomeSection>
  );
}
